/**
 * FranchiseApplyPage — `/franchise/applications/new`.
 *
 * Captures a new franchise outlet application and posts it to
 * `POST /v1/franchise/franchises/apply`. Address capture goes through the
 * shared `<AddressFields>` cascade (country → state → LGA → ward) so the
 * resulting row carries both the legacy free-text `state` / `lga` columns
 * and the new soft-FK `geo_*` columns introduced in the
 * `20260511_geo_columns.sql` migration.
 *
 * Free-text `state` / `lga` are kept (back-fill for legacy reports + the
 * National Map page) but are populated automatically from the geo cascade
 * on submit — operators never type them.
 */
import { type JSX, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  AddressFields,
  EMPTY_ADDRESS,
  validateAddress,
  type AddressValue,
} from '@upmp/ui';

import { applyForFranchise, type ApplyFranchiseDto, type TierCode } from '../../lib/api/franchise';
import { env } from '../../lib/env';
import { PageHeader } from '../shared/StaffUI';

const TIERS: readonly TierCode[] = ['TIER_1', 'TIER_2', 'TIER_3'];

export function FranchiseApplyPage(): JSX.Element {
  const navigate = useNavigate();
  const [legalName, setLegalName] = useState('');
  const [tradingName, setTradingName] = useState('');
  const [town, setTown] = useState('');
  const [requestedTier, setRequestedTier] = useState<TierCode>('TIER_1');
  const [ownerCustomerId, setOwnerCustomerId] = useState('');
  const [operatorIdRaw, setOperatorIdRaw] = useState('');
  const [parentId, setParentId] = useState('');
  const [payoutBankCode, setPayoutBankCode] = useState('');
  const [payoutAccountNumber, setPayoutAccountNumber] = useState('');
  const [address, setAddress] = useState<AddressValue>(EMPTY_ADDRESS);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  const apply = useMutation({
    mutationFn: (dto: ApplyFranchiseDto) => applyForFranchise(dto),
    onSuccess: (res) => {
      navigate(`/franchise/franchises/${encodeURIComponent(res.franchise.id)}`);
    },
    onError: (e: Error) => setSubmitErr(e.message),
  });

  const addressCheck = validateAddress(address);
  const operatorId = Number.parseInt(operatorIdRaw, 10);
  const operatorIdValid = Number.isSafeInteger(operatorId) && operatorId >= 0;
  const formValid =
    legalName.trim().length >= 2 &&
    ownerCustomerId.trim().length > 0 &&
    operatorIdValid &&
    addressCheck.ok;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setSubmitErr(null);
    if (!formValid) {
      setSubmitErr(addressCheck.ok ? 'Fill in the required fields.' : addressCheck.reason ?? 'Address invalid.');
      return;
    }
    const dto: ApplyFranchiseDto = {
      legalName: legalName.trim(),
      tradingName: tradingName.trim() || undefined,
      // Mirror the geo cascade selections into the legacy free-text columns
      // so existing dashboards (state breakdowns, LGA filters) keep working.
      state: address.stateName ?? '',
      lga: address.lgaName ?? '',
      town: town.trim() || undefined,
      address: {
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2,
        postalCode: address.postalCode,
        countryName: address.countryName,
        stateName: address.stateName,
        lgaName: address.lgaName,
        wardName: address.wardName,
      },
      ownerCustomerId: ownerCustomerId.trim(),
      requestedTier,
      parentId: parentId.trim() || undefined,
      payoutBankCode: payoutBankCode.trim() || undefined,
      payoutAccountNumber: payoutAccountNumber.trim() || undefined,
      operatorId,
      geoCountryId: address.countryId ?? undefined,
      geoCountryName: address.countryName ?? undefined,
      geoStateId: address.stateId ?? undefined,
      geoStateName: address.stateName ?? undefined,
      geoLgaId: address.lgaId ?? undefined,
      geoLgaName: address.lgaName ?? undefined,
      geoWardId: address.wardId ?? undefined,
      geoWardName: address.wardName ?? undefined,
    };
    apply.mutate(dto);
  };

  const inputCls =
    'block w-full rounded-md border border-border bg-white px-3 py-2 text-sm shadow-sm ' +
    'focus:border-nipost-green focus:ring-1 focus:ring-nipost-green';
  const labelCls = 'block text-xs font-medium text-gray-700 mb-1';

  return (
    <div className="space-y-6">
      <PageHeader
        title="New franchise application"
        description="Captures a DRAFT franchise outlet record. Submit + review happen on the onboarding page."
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        <section className="bg-white border border-border rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-900">Outlet identity</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Legal name *</label>
              <input
                type="text"
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                className={inputCls}
                required
                minLength={2}
                maxLength={200}
              />
            </div>
            <div>
              <label className={labelCls}>Trading name</label>
              <input
                type="text"
                value={tradingName}
                onChange={(e) => setTradingName(e.target.value)}
                className={inputCls}
                maxLength={200}
              />
            </div>
            <div>
              <label className={labelCls}>Requested tier *</label>
              <select
                value={requestedTier}
                onChange={(e) => setRequestedTier(e.target.value as TierCode)}
                className={inputCls}
              >
                {TIERS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Town (optional)</label>
              <input
                type="text"
                value={town}
                onChange={(e) => setTown(e.target.value)}
                className={inputCls}
                maxLength={120}
              />
            </div>
          </div>
        </section>

        <section className="bg-white border border-border rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-900">Outlet address</h2>
          <p className="text-xs text-muted">
            Select country, state, LGA and ward to set the outlet address.
          </p>
          <AddressFields
            geoBaseUrl={env.serviceGeoBaseUrl}
            value={address}
            onChange={setAddress}
            disabled={apply.isPending}
          />
        </section>

        <section className="bg-white border border-border rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-900">Ownership &amp; provisioning</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Owner customer id *</label>
              <input
                type="text"
                value={ownerCustomerId}
                onChange={(e) => setOwnerCustomerId(e.target.value)}
                className={`${inputCls} font-mono`}
                required
                maxLength={36}
                placeholder="UUID of the registered customer"
              />
            </div>
            <div>
              <label className={labelCls}>Operator id *</label>
              <input
                type="number"
                value={operatorIdRaw}
                onChange={(e) => setOperatorIdRaw(e.target.value)}
                className={inputCls}
                required
                min={0}
                step={1}
                placeholder="Provisioned upstream"
              />
            </div>
            <div>
              <label className={labelCls}>Parent franchise id (optional)</label>
              <input
                type="text"
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                className={`${inputCls} font-mono`}
                maxLength={36}
              />
            </div>
            <div>
              <label className={labelCls}>Payout bank code</label>
              <input
                type="text"
                value={payoutBankCode}
                onChange={(e) => setPayoutBankCode(e.target.value)}
                className={inputCls}
                maxLength={10}
              />
            </div>
            <div>
              <label className={labelCls}>Payout account number</label>
              <input
                type="text"
                value={payoutAccountNumber}
                onChange={(e) => setPayoutAccountNumber(e.target.value)}
                className={inputCls}
                maxLength={20}
              />
            </div>
          </div>
        </section>

        {submitErr ? <p className="text-sm text-danger">{submitErr}</p> : null}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => navigate('/franchise/applications')}
            className="px-3 py-2 border border-border rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!formValid || apply.isPending}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {apply.isPending ? 'Saving…' : 'Create draft franchise'}
          </button>
        </div>
      </form>
    </div>
  );
}
